// <pos-reservation-config/> — Owner 設定訂位行為
// 路徑：#/settings/reservation
// 主要功能：
//   - 用餐時長 / 提前預約限制
//   - 允許併桌 toggle
//   - 併桌組合管理（每組挑哪幾桌）
// see netlify/functions/reservation-config-upsert.js

import { getSupabase } from '../lib/supabase-client.js';
import {
  fetchReservationConfig, upsertReservationConfig, listStoreTables,
} from '../lib/api.js';

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

class PosReservationConfig extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      saving: false,
      error: null,
      msg: null,
      tenant_id: null,
      tenant_slug: null,
      store_id: null,
      tables: [],
      // form
      meal_duration_minutes: 90,
      max_advance_days: 30,
      min_advance_minutes: 60,
      allow_merge: false,
      merge_groups: [],   // [{ name?, table_ids: [] }]
    };
  }

  async connectedCallback() {
    this.render();
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('未登入');
      const payload = JSON.parse(atob(session.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/').padEnd(((session.access_token.split('.')[1].length + 3) >> 2) << 2, '=')));
      this.state.tenant_id = payload.tenant_id;
      // 同步拿 tenant slug 給「顧客訂位連結」卡片用
      const { data: tenantRow } = await supabase.from('tenants')
        .select('slug').eq('id', payload.tenant_id).maybeSingle();
      this.state.tenant_slug = tenantRow?.slug || null;
      const { data: store } = await supabase.from('stores')
        .select('id').eq('tenant_id', payload.tenant_id).eq('is_active', true)
        .order('created_at', { ascending: true }).limit(1).maybeSingle();
      if (!store) throw new Error('找不到店家店面');
      this.state.store_id = store.id;

      const [{ config }, { tables }] = await Promise.all([
        fetchReservationConfig({ storeId: store.id }),
        listStoreTables(),
      ]);
      this.state.meal_duration_minutes = config.meal_duration_minutes ?? 90;
      this.state.max_advance_days = config.max_advance_days ?? 30;
      this.state.min_advance_minutes = config.min_advance_minutes ?? 60;
      this.state.allow_merge = !!config.allow_merge;
      this.state.merge_groups = Array.isArray(config.merge_groups)
        ? config.merge_groups.map((g) => ({ name: g.name || '', table_ids: [...(g.table_ids || [])] }))
        : [];
      this.state.tables = (tables || []).filter((t) => t.is_active);
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async save() {
    if (this.state.saving) return;
    // 前端先驗：每組 ≥ 2 桌
    for (const g of this.state.merge_groups) {
      if ((g.table_ids || []).length < 2) {
        this.state.error = '每組併桌至少要 2 張桌位';
        this.render();
        return;
      }
    }

    this.state.saving = true;
    this.state.error = null;
    this.state.msg = null;
    this.render();
    try {
      await upsertReservationConfig({
        store_id: this.state.store_id,
        meal_duration_minutes: this.state.meal_duration_minutes,
        max_advance_days: this.state.max_advance_days,
        min_advance_minutes: this.state.min_advance_minutes,
        allow_merge: this.state.allow_merge,
        merge_groups: this.state.merge_groups
          .filter((g) => (g.table_ids || []).length >= 2)
          .map((g) => ({
            name: g.name?.trim() || undefined,
            table_ids: g.table_ids,
          })),
      });
      this.state.msg = '✓ 已儲存';
    } catch (err) {
      this.state.error = err.message_zh || err.message || '儲存失敗';
    } finally {
      this.state.saving = false;
      this.render();
    }
  }

  addGroup() {
    this.state.merge_groups.push({ name: '', table_ids: [] });
    this.render();
  }

  removeGroup(idx) {
    this.state.merge_groups.splice(idx, 1);
    this.render();
  }

  toggleTableInGroup(idx, tableId) {
    const g = this.state.merge_groups[idx];
    if (!g) return;
    const ids = g.table_ids || [];
    if (ids.includes(tableId)) {
      g.table_ids = ids.filter((id) => id !== tableId);
    } else {
      g.table_ids = [...ids, tableId];
    }
    this.render();
  }

  render() {
    const { loading, error, msg, saving, tables, merge_groups, allow_merge,
      meal_duration_minutes, max_advance_days, min_advance_minutes, tenant_slug } = this.state;

    if (loading) {
      this.shadowRoot.innerHTML = `<style>${this.styles()}</style><div class="loading">載入中…</div>`;
      return;
    }

    const tableMap = new Map(tables.map((t) => [t.id, t]));
    const reserveUrl = tenant_slug ? `${location.origin}/#/reserve/${encodeURIComponent(tenant_slug)}` : '';

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <main>
        <aside class="sub-nav">
          <div class="sub-nav__title">設定</div>
          <a class="sub-nav__item" href="#/settings">店家資料</a>
          <a class="sub-nav__item" href="#/settings/qr">桌位 QR Code</a>
          <a class="sub-nav__item is-active" href="#/settings/reservation">訂位設定</a>
          <a class="sub-nav__item" href="#/settings/stations">站點與通知</a>
          <a class="sub-nav__item" href="#/staff">員工</a>
          <a class="sub-nav__item" href="#/settings/ai-keys">AI Keys</a>
        </aside>

        <section class="panel">
          <div class="panel__head">
            <h1 class="panel__title">訂位設定</h1>
            <p class="panel__desc">設定用餐時長、提前預約限制、是否允許併桌</p>
          </div>

          ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
          ${msg ? `<div class="banner ok">${escapeHtml(msg)}</div>` : ''}

          ${reserveUrl ? `
          <div class="card url-card">
            <h2>顧客訂位網址</h2>
            <p class="hint">把這個連結貼到 IG / LINE / 名片 QR Code，顧客點開就能訂位。</p>
            <div class="url-row">
              <input class="url-row__input" type="text" readonly value="${escapeHtml(reserveUrl)}" data-field="reserve-url-display" />
              <button class="url-row__btn" data-action="copy-url" type="button">複製</button>
              <a class="url-row__btn url-row__btn--ghost" href="${escapeHtml(reserveUrl)}" target="_blank" rel="noopener">預覽 ↗</a>
            </div>
          </div>
          ` : `
          <div class="card url-card">
            <h2>顧客訂位網址</h2>
            <div class="hint" style="color:var(--danger)">尚未取得 tenant slug；請至「店家資料」設定後再回來</div>
          </div>
          `}

          <div class="card">
            <h2>基本</h2>

            <div class="field">
              <label>用餐預設時長（分鐘）</label>
              <input type="number" min="15" max="480" value="${meal_duration_minutes}" data-field="meal_duration_minutes" />
              <div class="hint">預設一筆訂位占桌多久（用來判斷時段衝突）</div>
            </div>

            <div class="field">
              <label>最早可提前幾天訂</label>
              <input type="number" min="1" max="365" value="${max_advance_days}" data-field="max_advance_days" />
            </div>

            <div class="field">
              <label>最少需提前幾分鐘訂</label>
              <input type="number" min="0" max="10080" value="${min_advance_minutes}" data-field="min_advance_minutes" />
              <div class="hint">避免客人臨櫃 5 分鐘前還在訂位</div>
            </div>
          </div>

          <div class="card">
            <h2>併桌</h2>

            <label class="toggle">
              <input type="checkbox" data-field="allow_merge" ${allow_merge ? 'checked' : ''} />
              <span>允許併桌（單桌不夠時嘗試指定組合）</span>
            </label>

            ${allow_merge ? `
              <div class="hint" style="margin-top:0.5rem">
                每組至少 2 桌，組內桌位需全部空閒、總座位 ≥ 派對人數才會被指派。
                建議：把實際擺設可拼起來的桌（中間沒走道、距離近）放同組。
              </div>

              <div class="groups">
                ${merge_groups.length === 0 ? `
                  <div class="empty">尚未建立任何併桌組合</div>
                ` : merge_groups.map((g, i) => {
                  const totalSeats = (g.table_ids || []).reduce((sum, id) => sum + (tableMap.get(id)?.seats || 0), 0);
                  return `
                    <div class="group">
                      <div class="group__head">
                        <input
                          class="group__name"
                          type="text"
                          placeholder="組合名稱（選填，例：大窗邊組）"
                          maxlength="50"
                          value="${escapeHtml(g.name || '')}"
                          data-group-idx="${i}"
                          data-field="group-name"
                        />
                        <span class="group__seats">合計 ${totalSeats} 位 · ${(g.table_ids || []).length} 桌</span>
                        <button class="rm-btn" data-action="rm-group" data-idx="${i}" aria-label="刪除組合">×</button>
                      </div>
                      <div class="group__tables">
                        ${tables.map((t) => {
                          const checked = (g.table_ids || []).includes(t.id);
                          return `
                            <label class="t-chip ${checked ? 'is-on' : ''}">
                              <input type="checkbox" ${checked ? 'checked' : ''}
                                data-action="toggle-table" data-group-idx="${i}" data-table-id="${t.id}" />
                              桌 ${escapeHtml(t.number)}${t.seats ? ` (${t.seats} 位)` : ''}
                            </label>
                          `;
                        }).join('')}
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>

              <button class="add-group-btn" data-action="add-group">+ 新增併桌組合</button>
            ` : ''}
          </div>

          <div class="actions">
            <button class="save-btn" data-action="save" ${saving ? 'disabled' : ''}>${saving ? '儲存中…' : '儲存'}</button>
          </div>
        </section>
      </main>
    `;

    this.bind();
  }

  bind() {
    const root = this.shadowRoot;

    root.querySelectorAll('input[data-field]').forEach((el) => {
      el.addEventListener('change', (e) => {
        const f = el.dataset.field;
        if (f === 'allow_merge') {
          this.state.allow_merge = e.target.checked;
          this.render();
          return;
        }
        const n = parseInt(e.target.value, 10);
        if (Number.isFinite(n)) this.state[f] = n;
      });
    });

    root.querySelectorAll('input[data-field="group-name"]').forEach((el) => {
      el.addEventListener('input', (e) => {
        const idx = Number(el.dataset.groupIdx);
        if (this.state.merge_groups[idx]) {
          this.state.merge_groups[idx].name = e.target.value;
        }
      });
    });

    root.querySelectorAll('[data-action="toggle-table"]').forEach((el) => {
      el.addEventListener('change', () => {
        const idx = Number(el.dataset.groupIdx);
        const tid = el.dataset.tableId;
        this.toggleTableInGroup(idx, tid);
      });
    });

    root.querySelector('[data-action="add-group"]')?.addEventListener('click', () => this.addGroup());
    root.querySelectorAll('[data-action="rm-group"]').forEach((b) => {
      b.addEventListener('click', () => this.removeGroup(Number(b.dataset.idx)));
    });
    root.querySelector('[data-action="save"]')?.addEventListener('click', () => this.save());

    root.querySelector('[data-action="copy-url"]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const input = root.querySelector('[data-field="reserve-url-display"]');
      if (!input?.value) return;
      try {
        await navigator.clipboard.writeText(input.value);
        const old = btn.textContent;
        btn.textContent = '已複製 ✓';
        setTimeout(() => { btn.textContent = old; }, 1500);
      } catch {
        input.select();
        document.execCommand('copy');
        btn.textContent = '已複製 ✓';
        setTimeout(() => { btn.textContent = '複製'; }, 1500);
      }
    });
  }

  styles() {
    return `
      :host { display: block; min-height: 100vh; background: var(--bg-base, #faf8f4); color: var(--fg-base, #2a2a2a); }
      main { display: grid; grid-template-columns: 200px 1fr; gap: 0; max-width: 1100px; margin: 0 auto; }
      @media (max-width: 768px) { main { grid-template-columns: 1fr; } }
      .loading { padding: 4rem 1rem; text-align: center; color: #888; }
      .sub-nav { padding: var(--s-4, 1rem) var(--s-2, 0.5rem); border-right: 1px solid var(--border, #eee); }
      .sub-nav__title { font-size: .75rem; text-transform: uppercase; letter-spacing: 2px; color: var(--fg-dim, #aaa); padding: 0 var(--s-3, 0.75rem) var(--s-2, 0.5rem); }
      .sub-nav__item { display: block; padding: var(--s-3, 0.75rem) var(--s-4, 1rem); color: var(--fg-muted, #666); text-decoration: none; border-radius: var(--r-md, 8px); font-size: .9rem; }
      .sub-nav__item:hover { background: var(--bg-elevated, #fff); color: var(--fg-base, #2a2a2a); }
      .sub-nav__item.is-active { background: var(--bg-elevated, #fff); color: var(--brand-primary, #c97b4f); border-left: 3px solid var(--brand-primary, #c97b4f); padding-left: calc(var(--s-4, 1rem) - 3px); }
      .panel { padding: 2rem 1.5rem; }
      .panel__title { margin: 0; font-size: 1.5rem; }
      .panel__desc { color: #888; margin: 0.25rem 0 1.5rem; font-size: 0.95rem; }
      .banner { padding: 0.75rem 1rem; border-radius: 8px; margin-bottom: 1rem; font-size: 0.9rem; }
      .banner.err { background: #fdecec; color: #c0392b; }
      .banner.ok { background: #ebfaed; color: #1f7a3d; }
      .card { background: #fff; border: 1px solid #eee; border-radius: 12px; padding: 1.25rem 1.5rem; margin-bottom: 1.25rem; }
      .card h2 { margin: 0 0 1rem; font-size: 1.05rem; color: #555; }
      .url-card { background: linear-gradient(135deg, #fff 60%, rgba(201,123,79,.04) 100%); border-color: #f0d9c8; }
      .url-row { display: flex; gap: 0.5rem; flex-wrap: wrap; align-items: center; }
      .url-row__input { flex: 1 1 320px; min-width: 0; padding: 0.55rem 0.75rem; border: 1px solid #ddd; border-radius: 8px; font-family: var(--font-mono, ui-monospace, monospace); font-size: 0.85rem; background: #fafafa; color: #333; }
      .url-row__btn { flex: 0 0 auto; padding: 0.55rem 1rem; border: none; border-radius: 8px; background: var(--brand-primary, #c97b4f); color: #fff; font-weight: 600; font-size: .9rem; cursor: pointer; text-decoration: none; line-height: 1; display: inline-flex; align-items: center; gap: 0.25rem; }
      .url-row__btn:hover { opacity: .9; }
      .url-row__btn--ghost { background: transparent; color: var(--brand-primary, #c97b4f); border: 1px solid var(--brand-primary, #c97b4f); }
      .field { margin-bottom: 1rem; }
      .field label { display: block; font-size: 0.9rem; margin-bottom: 0.4rem; color: #555; font-weight: 500; }
      .field input { width: 200px; padding: 0.5rem 0.75rem; border: 1px solid #ddd; border-radius: 8px; font-size: 0.95rem; }
      .field input:focus { outline: none; border-color: #c97b4f; }
      .hint { color: #888; font-size: 0.8rem; margin-top: 0.3rem; }
      .toggle { display: flex; align-items: center; gap: 0.6rem; cursor: pointer; }
      .toggle input { width: 18px; height: 18px; }
      .groups { margin-top: 1rem; display: flex; flex-direction: column; gap: 0.85rem; }
      .empty { color: #999; padding: 1rem; text-align: center; background: #fafafa; border-radius: 8px; }
      .group { border: 1px solid #ddd; border-radius: 10px; padding: 0.85rem 1rem; background: #fafafa; }
      .group__head { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.6rem; }
      .group__name { flex: 1; padding: 0.4rem 0.6rem; border: 1px solid #ccc; border-radius: 6px; font-size: 0.9rem; }
      .group__seats { color: #666; font-size: 0.85rem; white-space: nowrap; }
      .rm-btn { width: 28px; height: 28px; border-radius: 50%; border: 1px solid #ddd; background: #fff; cursor: pointer; font-size: 1.1rem; color: #c0392b; }
      .group__tables { display: flex; flex-wrap: wrap; gap: 0.4rem; }
      .t-chip { display: inline-flex; align-items: center; padding: 0.4rem 0.7rem; background: #fff; border: 1px solid #ddd; border-radius: 999px; font-size: 0.85rem; cursor: pointer; }
      .t-chip.is-on { background: #c97b4f; color: #fff; border-color: #c97b4f; }
      .t-chip input { display: none; }
      .add-group-btn { margin-top: 0.85rem; padding: 0.6rem 1rem; background: #fff; border: 1px dashed #c97b4f; color: #c97b4f; border-radius: 8px; cursor: pointer; font-size: 0.9rem; }
      .add-group-btn:hover { background: #fff7f3; }
      .actions { display: flex; justify-content: flex-end; gap: 0.5rem; padding-top: 1rem; }
      .save-btn { padding: 0.7rem 1.5rem; background: #c97b4f; color: #fff; border: 0; border-radius: 8px; font-size: 0.95rem; font-weight: 500; cursor: pointer; }
      .save-btn:disabled { opacity: 0.6; cursor: wait; }
    `;
  }
}

if (!customElements.get('pos-reservation-config')) {
  customElements.define('pos-reservation-config', PosReservationConfig);
}
